import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface BaseButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
}

interface StandardButtonProps extends BaseButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
}

interface IconButtonProps extends BaseButtonProps {
  variant?: 'icon';
  icon: React.ReactNode;
  tooltip?: string;
}

type ButtonProps = StandardButtonProps | IconButtonProps;

const Button: React.FC<ButtonProps> = (props) => {
  const { colors } = useTheme();
  const { children, onClick, disabled = false, className = '', type = 'button' } = props;

  // Icon Button
  if (props.variant === 'icon') {
    const iconProps = props as IconButtonProps;
    return (
      <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className={`p-2 rounded-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        style={{
          backgroundColor: colors.surface,
          color: colors.text,
        }}
        title={iconProps.tooltip}
      >
        {iconProps.icon}
      </button>
    );
  }

  // Standard Button
  const standardProps = props as StandardButtonProps;
  const { variant = 'secondary', size = 'md' } = standardProps;

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: colors.accent,
          color: '#ffffff',
          hover: { backgroundColor: colors.accent + 'dd' },
        };
      case 'secondary':
        return {
          backgroundColor: colors.surface,
          color: colors.text,
          hover: { backgroundColor: colors.surfaceHover },
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: colors.textSecondary,
          hover: { backgroundColor: colors.surface },
        };
      case 'danger':
        return {
          backgroundColor: colors.danger,
          color: '#ffffff',
          hover: { backgroundColor: colors.danger + 'dd' },
        };
      default:
        return {
          backgroundColor: colors.surface,
          color: colors.text,
          hover: { backgroundColor: colors.surfaceHover },
        };
    }
  };

  const variantStyles = getVariantStyles();

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`font-medium rounded-md transition-all duration-200 border hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${sizeClasses[size]} ${className}`}
      style={{
        backgroundColor: variantStyles.backgroundColor,
        color: variantStyles.color,
        borderColor: variant === 'ghost' ? colors.border : 'transparent',
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = variantStyles.hover.backgroundColor;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = variantStyles.backgroundColor;
        }
      }}
    >
      {children}
    </button>
  );
};

// Button Group Component
interface ButtonGroupProps {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  gap?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  align = 'left',
  gap = 'md',
  className = '',
}) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  const gapClasses = {
    sm: 'gap-2',
    md: 'gap-3',
    lg: 'gap-4',
  };

  return (
    <div className={`flex flex-wrap items-center ${alignClasses[align]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

// Toggle Button Component
interface ToggleButtonProps {
  options: { value: string; label: string }[];
  selected: string;
  onChange: (value: string) => void;
  className?: string;
}

export const ToggleButton: React.FC<ToggleButtonProps> = ({
  options,
  selected,
  onChange,
  className = '',
}) => {
  const { colors } = useTheme();

  return (
    <div 
      className={`inline-flex rounded-md border ${className}`} 
      style={{ borderColor: colors.borderLight }}
    >
      {options.map((option, index) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
            index === 0 ? 'rounded-l-md' : ''
          } ${
            index === options.length - 1 ? 'rounded-r-md' : ''
          }`}
          style={{
            backgroundColor: selected === option.value ? colors.accent : 'transparent',
            color: selected === option.value ? '#ffffff' : colors.textSecondary,
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

// Improved Native Slider Component
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  className?: string;
}

export const SliderInput: React.FC<SliderInputProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit,
  className = '',
}) => {
  const { colors } = useTheme();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(e.target.value));
  };

  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium" style={{ color: colors.text }}>
          {label}
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="number"
            value={value}
            onChange={handleChange}
            min={min}
            max={max}
            step={step}
            aria-label={`${label} 数值输入`}
            className="w-20 px-2 py-1 text-sm border rounded-md text-right"
            style={{
              backgroundColor: colors.surface,
              borderColor: colors.border,
              color: colors.text,
            }}
          />
          {unit && (
            <span className="text-sm" style={{ color: colors.textMuted }}>
              {unit}
            </span>
          )}
        </div>
      </div>
      
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          aria-label={`${label} 滑块控制`}
          className="w-full h-2 rounded-lg appearance-none cursor-pointer slider-custom"
          style={{
            background: `linear-gradient(to right, ${colors.accent} 0%, ${colors.accent} ${percentage}%, ${colors.border} ${percentage}%, ${colors.border} 100%)`,
          }}
        />
      </div>
      
      <div className="flex justify-between text-xs" style={{ color: colors.textMuted }}>
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
};

// Loading Button Component
interface LoadingButtonProps extends StandardButtonProps {
  loading?: boolean;
  loadingText?: string;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  loadingText = '加载中...',
  children,
  disabled,
  ...props
}) => {
  return (
    <Button {...props} disabled={disabled || loading}>
      {loading ? (
        <div className="flex items-center space-x-2">
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>{loadingText}</span>
        </div>
      ) : (
        children
      )}
    </Button>
  );
};

export default Button; 